<template>
  <div
    v-if="isLoaded"
    id="login"
    class="d-flex justify-center align-center flex-column"
  >
    <div>
      <v-alert closable class="mb-5 pa-3">
        <template v-slot:prepend>
          <v-icon
            icon="$mdiInformationSlabCircleOutline"
            size="40"
            color="#0073bb"
          />
        </template>
        <strong
          >WVS2カスタマーコントローラ 旧Webページ
          ご利用者さまへのお知らせ</strong
        >
        <p>
          WVS2カスタマーコントローラの新設備への更改に伴い、本Webページに統一いたしました。
        </p>
        <p>
          そのため、旧ページ（https://wvs2.kddi.ne.jp/online/login.html）へアクセスされた場合、
        </p>
        <p>自動的に本ページへリダイレクトされます。</p>
        <p>なお、ログイン情報に変更はございません。</p>
        <p>
          操作方法につきましては、
          <a
            href="https://wvs2document.com/pre-first/"
            target="_blank"
            rel="noopener noreferrer"
            >Web操作マニュアル</a
          >
          をご確認ください。
        </p>
      </v-alert>
    </div>
    <v-card variant="outlined" class="border pa-3">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form
          novalidate
          @submit.stop.prevent="handleSubmit(() => submit(false))"
        >
          <v-card-text>
            <div>
              <validation-provider
                name="loginId"
                label="ログインID"
                v-model="form.loginId"
                :rules="{ required: true, max: { length: 18 } }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-badge
                  item="ログインID"
                  :isRequired="false"
                  :is-info="false"
                />
                <app-form-input
                  v-bind="componentField"
                  :error-messages="errorMessage"
                  :field-meta="meta"
                />
              </validation-provider>
            </div>
            <div>
              <validation-provider
                name="password"
                label="パスワード"
                v-model="form.password"
                :rules="{
                  required: true,
                  between_length: { min: 8, max: 20 },
                }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-badge
                  item="パスワード"
                  :isRequired="false"
                  :is-info="false"
                />
                <app-form-input
                  type="password"
                  v-bind="componentField"
                  :error-messages="errorMessage"
                  :field-meta="meta"
                />
              </validation-provider>
            </div>
            <div>
              <router-link
                to="/password/one-time-url"
                custom
                v-slot="{ navigate }"
              >
                <span
                  class="item-link"
                  @click="navigate"
                  @keydown="() => navigate"
                  role="link"
                  >パスワードの再発行はこちらへ</span
                >
              </router-link>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-list-item class="w-100">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat"
                  >ログイン</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </v-card>
  </div>
</template>

<script lang="ts">
import {
  ApiConflictError,
  ApiServiceUnavailableError,
  ApiUnauthorizedError,
  NetworkError,
} from "@/error/errors";
import PasswordExpiredAlert from "@/modals/common/PasswordExpiredAlert.vue";
import AgreeStatusChangeConfirm from "@/modals/agreement/AgreeStatusChangeConfirm.vue";
import { defineComponent } from "vue";

export default defineComponent({
  name: "LoginView",
  data() {
    return {
      form: {
        loginId: "",
        password: "",
      },
      isLoaded: false,
    };
  },
  async mounted() {
    try {
      await this.$api.healthCheck.healthCheck();
    } catch (e) {
      // 407,503の場合は全体のエラーに任せる、それ以外はシステムエラー
      if (
        e instanceof ApiServiceUnavailableError ||
        e instanceof NetworkError
      ) {
        throw e;
      } else {
        console.error(e);
        await this.$router.push({ path: "/system/error" });
        return;
      }
    }
    this.isLoaded = true;
  },
  methods: {
    async submit(isForceLogin: boolean) {
      try {
        const result = await this.$api.auth.login({
          ...this.form,
          isForceLogin,
        });

        await this.$store.dispatch("user/login", result);
        await this.$router.push({ path: "/" });
        if (result.passwordLimit >= 1 && result.passwordLimit <= 14) {
          await this.$modal.show(PasswordExpiredAlert, { login: result });
        }
        await this.agreeStatusChange();
      } catch (e) {
        if (e instanceof ApiUnauthorizedError) {
          if (e.data.errorCode === "CC01004W") {
            await this.$router.push(
              `/password/one-time-url?expired=true&loginId=${this.form.loginId}`
            );
            return;
          }
        } else if (e instanceof ApiConflictError) {
          if (e.data.errorCode === "A111001W") {
            await this.$confirm(
              "同一アカウントによるログインセッションが残っています。\n" +
                "現在残っているセッションを切断し、新しくログインしますか？",
              "強制ログイン確認"
            );
            await this.submit(true);
            return;
          }
        }
        throw e;
      }
    },
    /**
     * 同意履歴の掲載開始日が最新のレコードの状態が「未同意」の場合
     * 「同意/同意詳細」画面を表示する
     * */
    async agreeStatusChange() {
      const agreement = await this.$api.agreement.getAll();
      // この時点ではstoreが使えないので取得する。
      const loginUser = await this.$api.information.getLoginUser();
      // 同意履歴が0件以外で且つ法人管理者権限の場合のみ表示対象
      if (
        agreement.agreementList.length !== 0 &&
        loginUser.roleCode === "C0001"
      ) {
        const agreeFirst = agreement.agreementList.sortBy([
          "postStartDate",
          "desc",
        ])[0];
        if (agreeFirst.agreementStatus === null) {
          const agreementText = (
            await this.$api.agreement.getAgreementDetail(
              agreeFirst.agreementSeq
            )
          ).agreementText;
          await this.$modal.show(AgreeStatusChangeConfirm, {
            agreementItem: agreeFirst,
            agreementKeyId: agreement.agreementKeyId,
            agreementText: agreementText,
          });
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
#login {
  height: 100%;

  .v-card {
    width: 500px;
  }
  .v-alert {
    width: 740px;
    border: 1px solid #0073bb !important;
    border-radius: 8px;
    font-size: 14px;
    color: #000 !important;
    background-color: #f1faff !important;
    strong {
      font-size: 18px;
    }
  }
}
</style>
